<template>
  <CCardBody>
    <CDataTable
      class="custom-pagination"
      :items="ticketsData"
      :fields="fields"
      table-filter
      items-per-page-select
      :items-per-page="5"
      hover
      pagination
    >
      
      <template #ticketNumber="{ item }">
        <td>
          <div class="font-weight-bold text-primary">{{ item.ticketNumber || '-' }}</div>
        </td>
      </template>

      <template #reported_datetime="{ item }">
        <td>
          <div>{{ formatDate(item.userPart.dateTimeInformed) }}</div>
        </td>
      </template>

      <template #username="{ item }">
        <td>
          <div class="font-weight-bold">{{ item.username }}</div>
          <div v-if="item.onBehalfOf" class="small text-danger">
            (แจ้งแทน: {{ item.onBehalfOf }})
          </div>
          <div class="small text-muted">{{ item.userEmail }}</div>
          <span v-if="item._role === 'reporter'" class="role-badge badge-reporter">แจ้งซ่อม</span>
          <span v-else class="role-badge badge-assignee">รับงาน</span>
        </td>
      </template>

      <template #issue_details="{ item }">
        <td>
          <div>
            <span class="text-primary font-weight-bold">{{ getCategoryName(item) }}</span> | 
            <span>{{ getSubCategoryName(item) }}</span>
          </div>
        </td>
      </template>

      <template #rating="{ item }">
        <td class="text-center">
          <div style="white-space: nowrap;">
            <span
              v-for="n in 5"
              :key="n"
              style="font-size: 18px; margin: 0 1px;"
              :style="{ color: n <= getRatingStars(item) ? '#FFD700' : '#E4E5E9' }"
            >
              ★
            </span>
          </div>
        </td>
      </template>

      <template #actions="{ item }">
        <td class="text-center">
          <CButton color="info" variant="outline" size="sm" class="circle-btn" @click="onRowClick(item)"
            title="ดูรายละเอียด">
            <CIcon name="cil-magnifying-glass" size="sm" />
          </CButton>
        </td>
      </template>
    </CDataTable>
    
  </CCardBody>
</template>

<style>
/* CSS คงเดิม */
.custom-pagination .page-item.active .page-link {
  background-color: #2ba9a5 !important;
  border-color: #2ba9a5 !important;
  color: #ffffff;
}
.custom-pagination .page-item .page-link {
  color: #414141;
}
.custom-pagination .page-item:not(.active) .page-link:hover {
  background-color: #f0f0f0;
}

.circle-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.role-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  margin-top: 3px;
}

.badge-reporter {
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}

.badge-assignee {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}
</style>

<script>
import { mapGetters } from "vuex";

// ✅ แก้ไข fields: เพิ่ม ticketNumber
const fields = [
  { key: "ticketNumber", label: "เลขที่ใบแจ้งซ่อม", _style: "width:15%;" },
  { key: "reported_datetime", label: "วัน/เวลา แจ้ง", _style: "width:15%;" },
  { key: "username", label: "ผู้แจ้ง", _style: "width:20%;" },
  { key: "issue_details", label: "รายละเอียด", _style: "width:30%;" }, 
  {
    key: "rating",
    label: "ความพึงพอใจ",
    _style: "width:15%; text-align:center;",
    class: "text-center"
  },
  { key: "actions", label: "", _style: "width:5%;", sorter: false, filter: false },
];

export default {
  name: "DepartmentHistoryTable",
  data() {
    return {
      ticketsData: [],
      fields,
    };
  },
  computed: {
    ...mapGetters("auth", ["profile"]),
  },
  watch: {
    profile(val) {
      if (val) this.loadTechnicianHistory();
    }
  },
  mounted() {
    if (this.profile) {
      this.loadTechnicianHistory();
    }
  },
  methods: {
    async loadTechnicianHistory() {
      try {
        if (!this.profile || !this.profile._id) {
          console.error("ไม่พบข้อมูลผู้ใช้งาน");
          return;
        }

        const technicianId = this.profile._id;

        // ดึงข้อมูลทั้ง 2 แหล่งพร้อมกัน
        const [assignedRes, reportedRes] = await Promise.all([
          fetch(`${process.env.VUE_APP_APP_URL}/ticket/technician/${technicianId}/assigned`),
          fetch(`${process.env.VUE_APP_APP_URL}/ticket/user/${technicianId}/history`),
        ]);

        const assignedResult = await assignedRes.json();
        const reportedResult = await reportedRes.json();

        let combined = [];

        // 1) งานที่ช่างรับ (welldone เท่านั้น)
        if (assignedResult.success && Array.isArray(assignedResult.data)) {
          const assignedTickets = assignedResult.data
            .filter(t => t.status === 'welldone')
            .map(ticket => {
              const user = ticket.userPart?.user || {};
              return {
                ...ticket,
                _role: 'assignee',
                username: `${user.name || ''} ${user.surname || ''}`.trim() || 'ไม่ระบุตัวตน',
                onBehalfOf: ticket.userPart?.onBehalfOf || '',
                userEmail: user.email || '-',
              };
            });
          combined = combined.concat(assignedTickets);
        }

        // 2) งานที่ช่างแจ้งเอง (เหมือน user history — status: welldone)
        if (reportedResult.success && Array.isArray(reportedResult.data)) {
          // หลีกเลี่ยง ticket ซ้ำ (กรณีช่างแจ้งแล้วตัวเองรับงาน)
          const existingIds = new Set(combined.map(t => String(t._id)));
          const reportedTickets = reportedResult.data
            .filter(t => !existingIds.has(String(t._id)))
            .map(ticket => {
              const user = ticket.userPart?.user || {};
              return {
                ...ticket,
                _role: 'reporter',
                username: `${user.name || ''} ${user.surname || ''}`.trim() || 'ไม่ระบุตัวตน',
                onBehalfOf: ticket.userPart?.onBehalfOf || '',
                userEmail: user.email || '-',
              };
            });
          combined = combined.concat(reportedTickets);
        }

        // เรียงตาม createdAt ล่าสุดก่อน
        combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        this.ticketsData = combined;

      } catch (error) {
        console.error("Failed to load technician history:", error);
      }
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes} น.`;
    },
    
    getCategoryName(item) {
      return item.userPart?.category?.departmentName || "ไม่ระบุ";
    },

    getSubCategoryName(item) {
      const sub = item.userPart?.subCategory;
      
      if (!sub) return "-";

      if (typeof sub === 'object') {
        return sub.subCategory || sub.name || sub.title || sub.subcategoryName || "-";
      }

      return sub;
    },

    getRatingStars(item) {
      return item.rating?.stars || 0;
    },
    onRowClick(item) {
      this.$emit("show-detail", item);
    },
  },
};
</script>